import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);

  const navLinks = [
    { name: 'Hjem', href: '/hjem' },
    { 
      name: 'Formål', 
      href: '/formal',
      subLinks: [
        { name: 'Vedtægter', href: '/vedtaegter' },
      ]
    },
    { 
      name: 'Aktiviteter', 
      href: '/aktiviteter-danmark',
      subLinks: [
        { name: 'Aktiviteter Danmark', href: '/aktiviteter-danmark' },
        { name: 'Aktiviteter Palæstina', href: '/aktiviteter-palaestina' },
      ]
    },
    { name: 'Bestyrelse', href: '/bestyrelse' },
    { name: 'Foto', href: '/foto' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  return (
    <nav className="sticky top-0 w-full z-50 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between h-auto lg:h-20 py-4 lg:py-0">
          <div className="flex items-center justify-between w-full lg:w-auto">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://assets.auxodev.dk/assets/c15c4fdc-8908-4ddc-a4c7-4bd7c9025029/dapao-logo-women-embrace.webp" 
                alt="DAPAO logo with two women embracing" 
                className="h-12 w-12 object-contain"
              />
              <div className="flex flex-col leading-none">
                <span className="text-2xl font-black tracking-tighter text-black transition-colors">DAPAO</span>
              </div>
            </Link>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-black hover:bg-gray-100 focus:outline-none"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Desktop & Tablet Menu */}
          <div className="hidden md:flex flex-wrap items-center justify-start lg:justify-end gap-x-6 lg:gap-x-8 mt-4 lg:mt-0 w-full lg:w-auto">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.subLinks ? (
                  <button className="flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-gray-600 group-hover:text-red-600 transition-colors py-2 lg:py-8">
                    {link.name}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </button>
                ) : (
                  <Link
                    to={link.href}
                    className="text-sm font-bold uppercase tracking-wider text-gray-600 hover:text-red-600 transition-colors py-2 lg:py-8"
                  >
                    {link.name}
                  </Link>
                )}

                {link.subLinks && (
                  <div className="absolute left-0 top-full w-56 bg-white border border-gray-100 shadow-xl rounded-b-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
                    {link.subLinks.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.href}
                        className="block px-4 py-3 text-xs font-semibold uppercase tracking-widest text-gray-600 hover:text-red-600 hover:bg-gray-50 transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="py-2 lg:py-0">
              <a
                href="#membership"
                className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold uppercase tracking-wider rounded-full text-white bg-red-600 hover:bg-red-700 transition-all duration-300 shadow-sm hover:-translate-y-1"
              >
                Støt nu
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-b border-gray-100 animate-in slide-in-from-top duration-300 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.subLinks ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                      className="flex items-center justify-between w-full px-3 py-4 text-base font-bold uppercase tracking-wider text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {link.name}
                      <ChevronDown className={`h-5 w-5 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === link.name && (
                      <div className="bg-gray-50 rounded-lg mb-2">
                        {link.subLinks.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.href}
                            className="block px-8 py-3 text-sm font-semibold uppercase tracking-wider text-gray-600 hover:text-red-600 transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.href}
                    className="block px-3 py-4 text-base font-bold uppercase tracking-wider text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="px-3 py-4">
              <a
                href="#membership"
                className="block w-full text-center px-5 py-3 border border-transparent text-base font-semibold uppercase tracking-wider rounded-full text-white bg-red-600 hover:bg-red-700 transition-all hover:-translate-y-1"
                onClick={() => setIsOpen(false)}
              >
                Støt nu
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
